import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { DialogService, FormNavigationService, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { QUESTION_PATHS } from '../../constants/app-data.constants';
import { DASHBOARD_FILTER_NAMES } from '../../constants/grid-data-filter.constants';

import { MaatUserGridFilterRequest } from '../../interfaces/maat-user-grid-filter-request.interface';
import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';

import { GridDataFilterHandler } from '../../helpers/grid-data-filter-handler';
import { GridDataClientHandler } from '../../helpers/grid-data-client-handler';

import { AppDataService } from '../../services/app-data.service';
import { ServiceWorkerService } from '../../services/service-worker.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-maat-user-list',
  templateUrl: './maat-user-list.component.html',
  styleUrls: ['./maat-user-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaatUserListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private loggedInUserProfile: LoggedInUserProfile;
  private filterHandler: GridDataFilterHandler<MaatUserGridFilterRequest>;

  private gridDataClientHandler: GridDataClientHandler;

  activeDialog: string = '';

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private formConfigurationParserService: FormConfigurationParserService) {
    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.maatUserList,
      () => API_ENDPOINTS.dashboard.getMaatUsers,
      true);

    this.filterHandler = new GridDataFilterHandler<MaatUserGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatUsers,
        searchQuestionPath: [...QUESTION_PATHS.maatUserList.search],
        filterQuestionPath: [...QUESTION_PATHS.maatUserList.filter],
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          roles: filterQuestionModel?.roles,
          hiipComplianceOfficer: filterQuestionModel?.hiipComplianceOfficer,
          hiipContractor: filterQuestionModel?.hiipContractor
        }),
        getDefaultFilterRequest: () => ({})
      });

    this.gridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatUserList.placeholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatUsers);
  }

  ngOnInit(): void {
    this.ignoreCanDeactivateGuard = true;
    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {

      this.loggedInUserProfile = waitResult.loggedInUserProfile;

      this.filterHandler.setInitialFilterRequest();
    });
  }

  ngOnDestroy(): void {
    this.gridDataClientHandler.unsubscribe();
    this.completeDestroy();
  }

  formLoad(event: any): void {
    this.configData = event.configData;
    this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.gridDataClientHandler.subscribe())
      .then(() => this.loadMaatUserGridDataOnFormLoad(this.filterHandler.getInitialFilterRequest()));
  }

  filterData(event: any): void {
    this.loadMaatUserGridData(this.filterHandler.saveFilterRequest(event.action));
  }

  actionButton_maatUsersPlaceholder_datagrid_edit_Clicked(event: any) {
    this.router.navigate(['/', ROUTE_CONSTANTS.maatUser.prefix, event.event.data.id]);
  }

  actionButton_maatUsersPlaceholder_datagrid_view_Clicked(event: any) {
    this.router.navigate(['/', ROUTE_CONSTANTS.maatUser.prefix, event.event.data.id]);
  }

  private loadMaatUserGridDataOnFormLoad(filterRequest: MaatUserGridFilterRequest): void {
    const onGridLoaded = () => this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridDataOnFormLoad(filterRequest, onGridLoaded);
  }

  private loadMaatUserGridData(filterRequest: MaatUserGridFilterRequest): void {
    const onGridLoaded = () => this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(filterRequest, onGridLoaded);
  }
}
